import { signal } from '@preact/signals';
import { ApiError, getAvatarUrl } from './api.js';

/**
 * **Les avatars, résolus une fois par révision.**
 *
 * Un avatar n'est pas une vignette, et les deux mécaniques ne se confondent pas :
 * une vignette se demande par lots de deux cents parce qu'une grille en peint
 * autant d'un coup, un avatar se demande à l'unité pour une poignée de personnes —
 * les membres d'un groupe, plafonnés à 50, et soi-même.
 *
 * La clé du cache est `uid#rev`, jamais `uid` seul, et c'est tout l'intérêt de
 * `avatarRev` : l'URL présignée vaut 15 minutes, mais l'**image** derrière peut
 * changer à tout moment. Sans le numéro dans la clé, changer son avatar le
 * laisserait ancien chez tous les membres de tous les groupes — jusqu'à la
 * prochaine ouverture d'onglet, ou pour toujours si le navigateur a mis l'image en
 * cache disque.
 *
 * `null` est une réponse, pas un échec : `404 avatar_absent` dit « cette personne
 * n'en a pas », et le mémoriser évite de le redemander à chaque rendu. Une erreur
 * de réseau, elle, n'est **pas** mémorisée — sinon un creux de connexion
 * effacerait les visages jusqu'au rechargement de la page.
 */

/** `uid#rev` → URL, ou `null` quand la personne n'a pas d'avatar. */
export const avatarUrls = signal<Record<string, string | null>>({});

const inFlight = new Set<string>();

const keyOf = (uid: string, rev: number) => `${uid}#${rev}`;

/**
 * L'URL de l'avatar de quelqu'un, ou `undefined` tant qu'on ne sait pas.
 *
 * `rev === 0` répond `null` **sans aucun appel** : c'est ce que `avatarRev` sert
 * dans la liste des membres — sans lui, afficher un groupe de cinquante personnes
 * demanderait cinquante requêtes pour apprendre que quarante-huit n'ont pas de
 * visage.
 */
export function avatarOf(uid: string, rev: number | undefined): string | null | undefined {
  if (!uid || !rev) return null;

  const key = keyOf(uid, rev);
  const known = avatarUrls.value[key];
  if (known !== undefined) return known;

  if (!inFlight.has(key)) {
    inFlight.add(key);
    void getAvatarUrl(uid)
      .then((res) => {
        avatarUrls.value = { ...avatarUrls.value, [key]: res.url };
      })
      .catch((err) => {
        // Seul un `404` est un fait durable sur la personne. Un `403` — le groupe
        // partagé vient d'être quitté — et une panne de réseau sont des faits sur
        // l'instant : les mémoriser ferait disparaître des visages pour de bon.
        if (err instanceof ApiError && err.status === 404) {
          avatarUrls.value = { ...avatarUrls.value, [key]: null };
        }
      })
      .finally(() => inFlight.delete(key));
  }
  return undefined;
}

/**
 * Oublie tout ce qu'on savait de l'avatar de quelqu'un.
 *
 * Appelé après **son propre** envoi : la révision change, donc les clés changent,
 * mais l'ancienne entrée resterait en mémoire pour rien — et surtout, un envoi qui
 * échouerait à mi-chemin laisserait une URL vers des octets qui ne sont plus ceux
 * qu'on croit.
 */
export function forgetAvatar(uid: string): void {
  const next: Record<string, string | null> = {};
  for (const [key, value] of Object.entries(avatarUrls.value)) {
    if (!key.startsWith(`${uid}#`)) next[key] = value;
  }
  avatarUrls.value = next;
}
